import React, { useEffect, useMemo, useRef, useState } from 'react';
import './custom.css';

const tabs = [
  { id: 'dashboard', label: 'Dashboard' },
  { id: 'collection', label: 'Coleccion' },
  { id: 'manual', label: 'Agregar Manual' },
  { id: 'scanner', label: 'Escaner IA' },
  { id: 'watchlist', label: 'Watchlist' },
  { id: 'settings', label: 'Configuracion' }
];

const COLLECTION_SORT_STORAGE_KEY = 'cardtracker.collection.sort';
const COLLECTION_FILTERS_STORAGE_KEY = 'cardtracker.collection.filters';

const initialStoreDraft = {
  name: '',
  url: '',
  provider: 'auto',
  keywordsText: '',
  excludedKeywordsText: '',
  useStoreOverrides: false,
  maxPriceClp: '',
  checkIntervalMinutes: 30,
  maxProductsPerCheck: 60,
  notifyOnNewProduct: true,
  notifyOnRestock: true,
  notifyOnPriceDrop: true
};

const initialWatchlistSettingsDraft = {
  globalKeywordsText:
    'one piece, op-, booster box, preventa, ST-, riftbound',
  globalExcludedKeywordsText: ''
};

function money(value) {
  return `$${Number(value || 0).toFixed(2)}`;
}

function pct(value) {
  const n = Number(value || 0);
  return `${n > 0 ? '+' : ''}${n.toFixed(2)}%`;
}

function clp(value) {
  if (value === null || value === undefined || value === '') {
    return 'N/D';
  }

  return `$${Number(value || 0).toLocaleString('es-CL', { maximumFractionDigits: 0 })} CLP`;
}

function dateTime(value) {
  if (!value) {
    return 'N/D';
  }

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) {
    return 'N/D';
  }

  return d.toLocaleString();
}

function buildPath(points, w, h) {
  if (!points.length) {
    return '';
  }

  const values = points.map((p) => Number(p.totalUsd || 0));
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = Math.max(max - min, 1);

  return points
    .map((p, idx) => {
      const x = (idx / Math.max(points.length - 1, 1)) * w;
      const y = h - ((Number(p.totalUsd || 0) - min) / span) * h;
      return `${idx === 0 ? 'M' : 'L'}${x.toFixed(2)} ${y.toFixed(2)}`;
    })
    .join(' ');
}

function getCollectionSortValue(item, key) {
  switch (key) {
    case 'card':
      return `${item.name || ''} ${item.cardCode || ''}`.trim();
    case 'set':
      return item.setCode || '';
    case 'print':
      return item.printInfo || '';
    case 'quantity':
      return Number(item.quantity || 0);
    case 'costUnit':
      return Number(item.avgAcquiredPriceUsd || 0);
    case 'price':
      return Number(item.unitPriceUsd || 0);
    case 'ath':
      return Number(item.athUsd || 0);
    case 'change24h':
      return Number(item.dailyChangePercent || 0);
    case 'invested':
      return Number(item.totalInvestedUsd || 0);
    case 'profit':
      return Number(item.profitUsd || 0);
    case 'value':
      return Number(item.totalValueUsd || 0);
    default:
      return '';
  }
}

function compareCollectionItems(a, b, key) {
  const av = getCollectionSortValue(a, key);
  const bv = getCollectionSortValue(b, key);

  if (typeof av === 'number' && typeof bv === 'number') {
    if (av !== bv) {
      return av - bv;
    }
  } else {
    const textCompare = String(av || '').localeCompare(String(bv || ''), 'es', { sensitivity: 'base', numeric: true });
    if (textCompare !== 0) {
      return textCompare;
    }
  }

  return String(a.cardCode || '').localeCompare(String(b.cardCode || ''), 'es', { sensitivity: 'base', numeric: true });
}

function loadStoredCollectionSort() {
  const fallback = { key: '', direction: 'asc' };

  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return fallback;
    }

    const raw = window.localStorage.getItem(COLLECTION_SORT_STORAGE_KEY);
    if (!raw) {
      return fallback;
    }

    const parsed = JSON.parse(raw);
    const key = typeof parsed?.key === 'string' ? parsed.key : '';
    const direction = parsed?.direction === 'desc' ? 'desc' : 'asc';
    return { key, direction };
  } catch {
    return fallback;
  }
}

function loadStoredCollectionFilters() {
  const fallback = {
    text: '',
    set: '',
    rarity: '',
    print: '',
    profitable: false
  };

  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return fallback;
    }

    const raw = window.localStorage.getItem(COLLECTION_FILTERS_STORAGE_KEY);
    if (!raw) {
      return fallback;
    }

    const parsed = JSON.parse(raw);
    return {
      text: typeof parsed?.text === 'string' ? parsed.text : '',
      set: typeof parsed?.set === 'string' ? parsed.set : '',
      rarity: typeof parsed?.rarity === 'string' ? parsed.rarity : '',
      print: typeof parsed?.print === 'string' ? parsed.print : '',
      profitable: Boolean(parsed?.profitable)
    };
  } catch {
    return fallback;
  }
}

export default function App() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');

  const [collection, setCollection] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [cards, setCards] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedCode, setSelectedCode] = useState('');
  const [manualQty, setManualQty] = useState(1);
  const [manualBuyPrice, setManualBuyPrice] = useState(0);
  const [manualPrintOptions, setManualPrintOptions] = useState([]);
  const [manualPrintKey, setManualPrintKey] = useState('');
  const [manualLoadingPrints, setManualLoadingPrints] = useState(false);

  const [scanFile, setScanFile] = useState(null);
  const [scanQty, setScanQty] = useState(1);
  const [rowModes, setRowModes] = useState({});
  const [rowDrafts, setRowDrafts] = useState({});
  const [rowPrintOptions, setRowPrintOptions] = useState({});
  const rowRefs = useRef({});
  const [historyCard, setHistoryCard] = useState('');
  const [historyRef, setHistoryRef] = useState('');
  const [historyPoints, setHistoryPoints] = useState([]);
  const [historyRange, setHistoryRange] = useState('7d');
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [historyLoadedAt, setHistoryLoadedAt] = useState('');
  const [initialCollectionFilters] = useState(loadStoredCollectionFilters);
  const [collectionFilterText, setCollectionFilterText] = useState(initialCollectionFilters.text);
  const [collectionFilterSet, setCollectionFilterSet] = useState(initialCollectionFilters.set);
  const [collectionFilterRarity, setCollectionFilterRarity] = useState(initialCollectionFilters.rarity);
  const [collectionFilterPrint, setCollectionFilterPrint] = useState(initialCollectionFilters.print);
  const [collectionFilterProfitable, setCollectionFilterProfitable] = useState(initialCollectionFilters.profitable);
  const [collectionSort, setCollectionSort] = useState(loadStoredCollectionSort);
  const [watchedStores, setWatchedStores] = useState([]);
  const [watchedProducts, setWatchedProducts] = useState([]);
  const [watchlistSettingsDraft, setWatchlistSettingsDraft] =
    useState(initialWatchlistSettingsDraft);
  const [telegramStatus, setTelegramStatus] = useState(null);
  const [codexStatus, setCodexStatus] = useState(null);
  const [storeDraft, setStoreDraft] = useState(initialStoreDraft);
  const [watchStockFilter, setWatchStockFilter] = useState('all');
  const [watchStoreFilter, setWatchStoreFilter] = useState('all');
  const [watchSearchText, setWatchSearchText] = useState('');
  const [watchProductPage, setWatchProductPage] = useState(1);
  const [selectedWatchProductKeys, setSelectedWatchProductKeys] =
    useState({});
  const [editingStoreId, setEditingStoreId] = useState('');
  const [storeEditDraft, setStoreEditDraft] = useState(null);
  const [telegramDraft, setTelegramDraft] = useState({ botToken: '', allowedChatId: '' });
  const [codexDraft, setCodexDraft] = useState({ enabled: '', validateLogin: true });
  const [codexLogin, setCodexLogin] = useState(null);

  const watchProductKey = (product) =>
    `${product.storeId}\u001f${product.externalId}`;

  const setCodeFromCardCode = (code) => {
    const normalized = String(code || '').trim().toUpperCase();
    const idx = normalized.indexOf('-');
    return idx > 0 ? normalized.slice(0, idx) : normalized;
  };

  const makePrintOptionKey = (opt) =>
    `${opt.variantNumber == null ? 'base' : `v${opt.variantNumber}`}|${opt.setCode || ''}|${opt.printInfo || ''}|${opt.priceUsd ?? ''}`;

  const samePrintOption = (opt, draft) => {
    if (draft?.printVariantNumber != null && opt?.variantNumber != null) {
      return Number(opt.variantNumber) === Number(draft.printVariantNumber);
    }

    return opt?.printInfo === draft?.printInfo &&
      (draft?.printPriceUsd == null || opt?.priceUsd === draft?.printPriceUsd);
  };

  const trendPath = useMemo(() => buildPath(dashboard?.portfolioTrend || [], 700, 220), [dashboard]);
  const historyPath = useMemo(
    () => buildPath((historyPoints || []).map((p) => ({ totalUsd: p.priceUsd })), 700, 160),
    [historyPoints]
  );
  const collectionSets = useMemo(
    () => Array.from(new Set((collection || []).map((x) => String(x.setCode || '').trim()).filter(Boolean))).sort(),
    [collection]
  );
  const collectionRarities = useMemo(
    () => Array.from(new Set((collection || []).map((x) => String(x.rarity || '').trim()).filter(Boolean))).sort(),
    [collection]
  );
  const filteredCollection = useMemo(() => {
    const text = collectionFilterText.trim().toLowerCase();
    const print = collectionFilterPrint.trim().toLowerCase();
    const items = (collection || []).filter((item) => {
      if (collectionFilterSet && String(item.setCode || '') !== collectionFilterSet) {
        return false;
      }
      if (collectionFilterRarity && String(item.rarity || '') !== collectionFilterRarity) {
        return false;
      }
      if (collectionFilterProfitable && Number(item.profitUsd || 0) <= 0) {
        return false;
      }
      if (print && !String(item.printInfo || '').toLowerCase().includes(print)) {
        return false;
      }
      if (!text) {
        return true;
      }

      return String(item.cardCode || '').toLowerCase().includes(text)
        || String(item.name || '').toLowerCase().includes(text)
        || String(item.setCode || '').toLowerCase().includes(text)
        || String(item.rarity || '').toLowerCase().includes(text)
        || String(item.printInfo || '').toLowerCase().includes(text);
    });

    if (!collectionSort.key) {
      return items;
    }

    const factor = collectionSort.direction === 'desc' ? -1 : 1;
    return [...items].sort((a, b) => compareCollectionItems(a, b, collectionSort.key) * factor);
  }, [
    collection,
    collectionFilterText,
    collectionFilterSet,
    collectionFilterRarity,
    collectionFilterPrint,
    collectionFilterProfitable,
    collectionSort
  ]);
  const totalCopies = useMemo(
    () => (collection || []).reduce((sum, item) => sum + Math.max(0, Number(item.quantity || 0)), 0),
    [collection]
  );
  const filteredCopies = useMemo(
    () => (filteredCollection || []).reduce((sum, item) => sum + Math.max(0, Number(item.quantity || 0)), 0),
    [filteredCollection]
  );

  const filteredWatchedProducts = useMemo(() => {
    const search = watchSearchText.trim().toLowerCase();

    return (watchedProducts || []).filter((product) => {
      if (
        watchStoreFilter !== 'all'
        && product.storeId !== watchStoreFilter
      ) {
        return false;
      }

      if (
        watchStockFilter === 'in'
        && product.inStock !== true
      ) {
        return false;
      }

      if (
        watchStockFilter === 'out'
        && product.inStock !== false
      ) {
        return false;
      }

      if (
        watchStockFilter === 'unknown'
        && (product.inStock === true || product.inStock === false)
      ) {
        return false;
      }

      if (search) {
        const store = watchedStores.find(
          (item) => item.id === product.storeId
        );

        const text = [
          product.title,
          product.url,
          store?.name
        ].join(' ').toLowerCase();

        if (!text.includes(search)) {
          return false;
        }
      }

      return true;
    });
  }, [
    watchedProducts,
    watchedStores,
    watchStockFilter,
    watchStoreFilter,
    watchSearchText
  ]);

  const watchPageSize = 80;
  const watchPageCount = Math.max(
    1,
    Math.ceil(filteredWatchedProducts.length / watchPageSize)
  );
  const safeWatchProductPage = Math.min(
    watchProductPage,
    watchPageCount
  );
  const pagedWatchedProducts = filteredWatchedProducts.slice(
    (safeWatchProductPage - 1) * watchPageSize,
    safeWatchProductPage * watchPageSize
  );

  const toggleCollectionSort = (key) => {
    setCollectionSort((current) => {
      if (current.key === key) {
        return { key, direction: current.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  const renderCollectionSortHeader = (label, key) => {
    const isActive = collectionSort.key === key;
    const nextDirection = isActive && collectionSort.direction === 'asc' ? 'desc' : 'asc';
    const icon = isActive ? (collectionSort.direction === 'asc' ? '▲' : '▼') : '↕';

    return (
      <th>
        <span className="th-sort">
          <span>{label}</span>
          <button
            type="button"
            className={`sort-toggle ${isActive ? 'active' : ''}`}
            onClick={() => toggleCollectionSort(key)}
            aria-label={`Ordenar ${label} (${nextDirection === 'asc' ? 'ascendente' : 'descendente'})`}
            title={`Ordenar ${label}`}
          >
            {icon}
          </button>
        </span>
      </th>
    );
  };

  const runTask = async (fn) => {
    try {
      setBusy(true);
      setMessage('');
      await fn();
    } catch (err) {
      setMessage(err.message || 'Error inesperado');
    } finally {
      setBusy(false);
    }
  };

  const loadCollection = async () => {
    const res = await fetch('/api/collection');
    const data = await res.json();
    setCollection(data);
  };

  const loadDashboard = async () => {
    const res = await fetch('/api/dashboard');
    const data = await res.json();
    setDashboard(data);
  };


  const loadWatchlist = async () => {
    const [storesRes, productsRes, settingsRes] =
      await Promise.all([
        fetch('/api/watchlist/stores'),
        fetch('/api/watchlist/products'),
        fetch('/api/watchlist/settings')
      ]);

    const stores = await storesRes.json();
    const products = await productsRes.json();
    const settings = await settingsRes.json();

    setWatchedStores(stores);
    setWatchedProducts(products);
    setWatchlistSettingsDraft({
      globalKeywordsText:
        (settings.globalKeywords || []).join(', '),
      globalExcludedKeywordsText:
        (settings.globalExcludedKeywords || []).join(', ')
    });
  };

  const loadSettings = async () => {
    const [telegramRes, codexRes] = await Promise.all([
      fetch('/api/watchlist/telegram/status'),
      fetch('/api/watchlist/codex/status')
    ]);
    setTelegramStatus(await telegramRes.json());
    setCodexStatus(await codexRes.json());
  };

  const searchCards = async (q) => {
    const params = new URLSearchParams({ query: q || '', page: '1', pageSize: '80' });
    const res = await fetch(`/api/cards?${params.toString()}`);
    const data = await res.json();
    setCards(data);
  };

  useEffect(() => {
    runTask(async () => {
      await Promise.all([loadCollection(), loadDashboard(), searchCards('')]);
    });
  }, []);

  useEffect(() => {
    if (activeTab !== 'collection') {
      return;
    }

    runTask(async () => {
      await loadCollection();
    });
  }, [activeTab]);


  useEffect(() => {
    if (activeTab !== 'watchlist') {
      return;
    }

    runTask(async () => {
      await loadWatchlist();
    });
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== 'settings') {
      return;
    }

    runTask(async () => {
      await loadSettings();
    });
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== 'collection') {
      return undefined;
    }

    const timer = setInterval(() => {
      const hasEditing = Object.values(rowModes).includes('editing');
      if (hasEditing) {
        return;
      }

      loadCollection().catch(() => {
        // silent refresh on collection tab
      });
    }, 30000);

    return () => clearInterval(timer);
  }, [activeTab, rowModes]);

  useEffect(() => {
    if (activeTab !== 'dashboard') {
      return undefined;
    }

    const timer = setInterval(() => {
      loadDashboard().catch(() => {
        // silent refresh on dashboard tab
      });
    }, 30000);

    return () => clearInterval(timer);
  }, [activeTab]);

  useEffect(() => {
    try {
      if (typeof window === 'undefined' || !window.localStorage) {
        return;
      }

      window.localStorage.setItem(COLLECTION_SORT_STORAGE_KEY, JSON.stringify(collectionSort));
    } catch {
      // ignore storage errors (private mode / blocked storage)
    }
  }, [collectionSort]);

  useEffect(() => {
    try {
      if (typeof window === 'undefined' || !window.localStorage) {
        return;
      }

      window.localStorage.setItem(COLLECTION_FILTERS_STORAGE_KEY, JSON.stringify({
        text: collectionFilterText,
        set: collectionFilterSet,
        rarity: collectionFilterRarity,
        print: collectionFilterPrint,
        profitable: collectionFilterProfitable
      }));
    } catch {
      // ignore storage errors (private mode / blocked storage)
    }
  }, [
    collectionFilterText,
    collectionFilterSet,
    collectionFilterRarity,
    collectionFilterPrint,
    collectionFilterProfitable
  ]);


  const parseKeywordText = (value) => String(value || '')
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean);

  const buildStorePayload = (draft) => ({
    name: draft.name,
    url: draft.url,
    provider: draft.provider || 'auto',
    keywords: parseKeywordText(draft.keywordsText),
    excludedKeywords: parseKeywordText(draft.excludedKeywordsText),
    useStoreOverrides: Boolean(draft.useStoreOverrides),
    maxPriceClp: draft.maxPriceClp === '' ? null : Number(draft.maxPriceClp || 0),
    checkIntervalMinutes: Number(draft.checkIntervalMinutes || 30),
    maxProductsPerCheck: Number(draft.maxProductsPerCheck || 60),
    notifyOnNewProduct: Boolean(draft.notifyOnNewProduct),
    notifyOnRestock: Boolean(draft.notifyOnRestock),
    notifyOnPriceDrop: Boolean(draft.notifyOnPriceDrop)
  });


  const storeToDraft = (store) => ({
    name: store.name || '',
    url: store.url || '',
    provider: store.provider || 'auto',
    keywordsText: (store.keywords || []).join(', '),
    excludedKeywordsText: (store.excludedKeywords || []).join(', '),
    useStoreOverrides: Boolean(store.useStoreOverrides),
    maxPriceClp: store.maxPriceClp ?? '',
    checkIntervalMinutes: store.checkIntervalMinutes || 30,
    maxProductsPerCheck: store.maxProductsPerCheck || 60,
    notifyOnNewProduct: Boolean(store.notifyOnNewProduct),
    notifyOnRestock: Boolean(store.notifyOnRestock),
    notifyOnPriceDrop: Boolean(store.notifyOnPriceDrop),
    enabled: Boolean(store.enabled)
  });

  const buildStoreUpdatePayload = (draft, store) => ({
    ...buildStorePayload(draft),
    enabled: draft.enabled ?? store.enabled,
    resetBaseline: false
  });

  const saveWatchlistSettings = () => runTask(async () => {
    const payload = {
      globalKeywords: parseKeywordText(
        watchlistSettingsDraft.globalKeywordsText
      ),
      globalExcludedKeywords: parseKeywordText(
        watchlistSettingsDraft.globalExcludedKeywordsText
      )
    };

    const res = await fetch('/api/watchlist/settings', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });

    const data = await res.json();

    if (!data.success) {
      throw new Error(
        data.message
        || 'No se pudieron guardar los filtros globales'
      );
    }

    setMessage(data.message);
    await loadWatchlist();
  });

  const addWatchedStore = (preset = null) => runTask(async () => {
    const draft = preset
      ? { ...initialStoreDraft, ...preset }
      : storeDraft;
    const payload = buildStorePayload(draft);
    if (!payload.url) {
      throw new Error('URL de tienda obligatoria');
    }

    const res = await fetch('/api/watchlist/stores', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    const data = await res.json();
    if (!data.success) {
      throw new Error(data.message || 'No se pudo agregar la tienda');
    }

    if (!preset) {
      setStoreDraft(initialStoreDraft);
    }
    setMessage('Tienda agregada. El primer scan crea baseline sin alertas.');
    await loadWatchlist();
  });

  const deleteWatchedStore = (id) => runTask(async () => {
    const res = await fetch(`/api/watchlist/stores/${encodeURIComponent(id)}`, { method: 'DELETE' });
    const data = await res.json();
    if (!data.success) {
      throw new Error(data.message || 'No se pudo eliminar la tienda');
    }
    await loadWatchlist();
  });

  const scanWatchedStore = (id) => runTask(async () => {
    const res = await fetch(`/api/watchlist/stores/${encodeURIComponent(id)}/scan`, { method: 'POST' });
    const data = await res.json();
    setMessage(data.message || 'Scan ejecutado');
    await loadWatchlist();
  });

  const updateWatchedStore = (store, payload) => runTask(async () => {
    const res = await fetch(`/api/watchlist/stores/${encodeURIComponent(store.id)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    const data = await res.json();
    if (!data.success) {
      throw new Error(data.message || 'No se pudo actualizar la tienda');
    }
    await loadWatchlist();
  });

  const toggleWatchedStore = (store) => {
    const draft = storeToDraft(store);
    updateWatchedStore(store, { ...buildStoreUpdatePayload(draft, store), enabled: !store.enabled });
  };

  const startStoreEdit = (store) => {
    setEditingStoreId(store.id);
    setStoreEditDraft(storeToDraft(store));
  };

  const cancelStoreEdit = () => {
    setEditingStoreId('');
    setStoreEditDraft(null);
  };

  const saveStoreEdit = (store) => runTask(async () => {
    const payload = buildStoreUpdatePayload(storeEditDraft || storeToDraft(store), store);
    const res = await fetch(`/api/watchlist/stores/${encodeURIComponent(store.id)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    const data = await res.json();
    if (!data.success) {
      throw new Error(data.message || 'No se pudo actualizar la tienda');
    }
    cancelStoreEdit();
    await loadWatchlist();
  });

  const toggleWatchProductSelection = (product) => {
    const key = watchProductKey(product);

    setSelectedWatchProductKeys((current) => {
      const next = { ...current };

      if (next[key]) {
        delete next[key];
      } else {
        next[key] = true;
      }

      return next;
    });
  };

  const selectVisibleWatchProducts = () => {
    const visible = pagedWatchedProducts;

    setSelectedWatchProductKeys((current) => {
      const next = { ...current };

      visible.forEach((product) => {
        next[watchProductKey(product)] = true;
      });

      return next;
    });
  };

  const clearWatchProductSelection = () => {
    setSelectedWatchProductKeys({});
  };

  const deleteSelectedWatchProducts = () => runTask(async () => {
    const selected = watchedProducts.filter(
      (product) =>
        selectedWatchProductKeys[watchProductKey(product)]
    );

    if (selected.length === 0) {
      throw new Error('No hay productos seleccionados');
    }

    if (!window.confirm(
      `¿Eliminar ${selected.length} producto(s) seleccionados?`
    )) {
      return;
    }

    const res = await fetch('/api/watchlist/products/delete', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        products: selected.map((product) => ({
          storeId: product.storeId,
          externalId: product.externalId
        }))
      })
    });

    const data = await res.json();

    if (!data.success) {
      throw new Error(
        data.message || 'No se pudieron eliminar los productos'
      );
    }

    clearWatchProductSelection();
    setMessage(data.message);
    await loadWatchlist();
  });

  const saveTelegramSettings = () => runTask(async () => {
    const payload = {
      botToken: telegramDraft.botToken.trim() || null,
      allowedChatId: telegramDraft.allowedChatId ? Number(telegramDraft.allowedChatId) : null
    };
    if (!payload.botToken && !payload.allowedChatId) {
      throw new Error('Ingresa token o chat id para guardar');
    }

    const res = await fetch('/api/watchlist/telegram/settings', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    const data = await res.json();
    if (!data.success) {
      throw new Error(data.message || 'No se pudo guardar Telegram');
    }
    setTelegramDraft({ botToken: '', allowedChatId: '' });
    setMessage('Configuracion de Telegram guardada. El bot la detecta automaticamente.');
    await loadSettings();
  });

  const saveCodexSettings = () => runTask(async () => {
    const enabled = codexDraft.enabled === '' ? null : codexDraft.enabled === 'true';
    const payload = {
      enabled,
      validateLogin: Boolean(codexDraft.validateLogin)
    };

    const res = await fetch('/api/watchlist/codex/settings', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    const data = await res.json();
    if (!data.success) {
      throw new Error(data.message || 'No se pudo guardar Codex');
    }
    setCodexDraft({ enabled: '', validateLogin: true });
    setMessage(data.message || 'Configuracion Codex guardada.');
    await loadSettings();
  });

  const validateCodexLogin = () => runTask(async () => {
    const res = await fetch('/api/watchlist/codex/validate', { method: 'POST' });
    const data = await res.json();
    if (!data.success) {
      throw new Error(data.message || 'Codex no autentico');
    }
    setMessage(data.message || 'Codex autenticado.');
    await loadSettings();
  });

  const startCodexLogin = () => runTask(async () => {
    const res = await fetch('/api/watchlist/codex/login/start', { method: 'POST' });
    const data = await res.json();
    setCodexLogin(data);
    if (!data.success) {
      throw new Error(data.message || 'No se pudo iniciar login Codex');
    }
    setMessage(data.message || 'Login Codex iniciado.');
    await loadSettings();
  });

  const syncCatalog = () => runTask(async () => {
    const res = await fetch('/api/cards/sync', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ force: true })
    });
    const data = await res.json();
    setMessage(`Sync completado. Catalogo: ${data.catalogCount || 0} cartas.`);
    await Promise.all([loadDashboard(), searchCards(search)]);
  });

  const addManual = () => runTask(async () => {
    if (!selectedCode) {
      throw new Error('Selecciona una carta');
    }

    const selectedPrint = manualPrintOptions.find((x) => makePrintOptionKey(x) === manualPrintKey) || null;
    const printHint = selectedPrint
      ? `${selectedPrint.variantNumber == null ? 'base' : `v=${selectedPrint.variantNumber}`} ${selectedPrint.printInfo || ''}`.trim()
      : '';

    const res = await fetch('/api/collection/manual', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        cardCode: selectedCode,
        quantity: Number(manualQty || 1),
        unitAcquiredPriceUsd: Math.max(0, Number(manualBuyPrice || 0)),
        printHint,
        tcgPlayerUrlHint: selectedPrint?.tcgPlayerUrl || ''
      })
    });
    const data = await res.json();
    if (!data.success) {
      throw new Error(data.message || 'No se pudo agregar');
    }

    setMessage(`Agregada ${selectedCode} a la coleccion.`);
    await Promise.all([loadCollection(), loadDashboard()]);
  });

  useEffect(() => {
    const normalizedCode = String(selectedCode || '').trim().toUpperCase();
    if (!normalizedCode) {
      setManualPrintOptions([]);
      setManualPrintKey('');
      return;
    }

    let cancelled = false;
    const loadPrints = async () => {
      try {
        setManualLoadingPrints(true);
        const res = await fetch(`/api/cards/${encodeURIComponent(normalizedCode)}/prints`);
        const data = await res.json();
        if (cancelled) {
          return;
        }

        const options = Array.isArray(data)
          ? data.filter((x) => x && x.setCode && x.printInfo)
          : [];
        setManualPrintOptions(options);
        setManualPrintKey(options.length > 0 ? makePrintOptionKey(options[0]) : '');
      } catch {
        if (!cancelled) {
          setManualPrintOptions([]);
          setManualPrintKey('');
        }
      } finally {
        if (!cancelled) {
          setManualLoadingPrints(false);
        }
      }
    };

    loadPrints();
    return () => {
      cancelled = true;
    };
  }, [selectedCode]);

  const selectedManualPrint = manualPrintOptions.find((x) => makePrintOptionKey(x) === manualPrintKey) || null;

  const removeCard = (entryKey, cardCode) => runTask(async () => {
    const accepted = window.confirm(`¿Seguro que quieres borrar ${cardCode} de la colección?`);
    if (!accepted) {
      return;
    }

    await fetch(`/api/collection/${encodeURIComponent(entryKey)}`, { method: 'DELETE' });
    await Promise.all([loadCollection(), loadDashboard()]);
    setMessage(`${cardCode} eliminada de la coleccion.`);
  });

  const isEditingRow = (entryKey) => rowModes[entryKey] === 'editing';

  const focusRowEditor = (entryKey) => {
    requestAnimationFrame(() => {
      const rowEl = rowRefs.current[entryKey];
      if (!rowEl) {
        return;
      }

      rowEl.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      const firstField = rowEl.querySelector('input, select');
      if (firstField) {
        firstField.focus();
      }
    });
  };

  const loadRowPrintOptions = async (rowKey, targetCardCode, fallbackItem) => {
    try {
      const normalizedCode = String(targetCardCode || '').trim().toUpperCase();
      if (!normalizedCode) {
        setRowPrintOptions((prev) => ({ ...prev, [rowKey]: [] }));
        return;
      }

      const res = await fetch(`/api/cards/${encodeURIComponent(normalizedCode)}/prints`);
      const data = await res.json();
      const options = Array.isArray(data)
        ? data.filter((x) => x && x.setCode && x.printInfo)
        : [];
      setRowPrintOptions((prev) => ({ ...prev, [rowKey]: options }));

      if (options.length > 0) {
        const current = getRowDraft(rowKey, fallbackItem);
        const exists = options.some(
          (x) => samePrintOption(x, current)
        );
        if (!exists) {
          const first = options[0];
          setRowDraftField(rowKey, 'setCode', first.setCode);
          setRowDraftField(rowKey, 'printInfo', first.printInfo);
          setRowDraftField(rowKey, 'printPriceUsd', first.priceUsd ?? null);
          setRowDraftField(rowKey, 'printVariantNumber', first.variantNumber ?? null);
          setRowDraftField(rowKey, 'tcgPlayerUrl', first.tcgPlayerUrl || '');
          setRowDraftField(rowKey, 'printOptionKey', makePrintOptionKey(first));
        } else {
          const matched = options.find(
            (x) => samePrintOption(x, current)
          );
          if (matched) {
            setRowDraftField(rowKey, 'printOptionKey', makePrintOptionKey(matched));
            setRowDraftField(rowKey, 'printVariantNumber', matched.variantNumber ?? null);
            setRowDraftField(rowKey, 'tcgPlayerUrl', matched.tcgPlayerUrl || '');
          }
        }
      }
    } catch {
      setRowPrintOptions((prev) => ({ ...prev, [rowKey]: [] }));
      setMessage(`No pude cargar opciones de print para ${targetCardCode}.`);
    }
  };

  const startRowEdit = async (item) => {
    const rowKey = item.entryKey || item.cardCode;
    setRowModes((prev) => ({ ...prev, [rowKey]: 'editing' }));
    setRowDrafts((prev) => ({
      ...prev,
      [rowKey]: {
        cardCode: item.cardCode || '',
        setCode: item.setCode || setCodeFromCardCode(item.cardCode),
        printInfo: item.printInfo || '',
        printPriceUsd: item.printPriceUsd ?? null,
        printVariantNumber: item.printVariantNumber ?? null,
        tcgPlayerUrl: item.tcgPlayerUrl || '',
        printOptionKey: '',
        quantity: Number(item.quantity || 0),
        totalInvestedUsd: Number(item.totalInvestedUsd || 0)
      }
    }));
    await loadRowPrintOptions(rowKey, item.cardCode, item);
    focusRowEditor(rowKey);
  };

  const cancelRowEdit = (cardCode) => {
    setRowModes((prev) => ({ ...prev, [cardCode]: 'display' }));
    setRowDrafts((prev) => {
      const next = { ...prev };
      delete next[cardCode];
      return next;
    });
  };

  const getRowDraft = (cardCode, fallbackItem) => {
    if (Object.prototype.hasOwnProperty.call(rowDrafts, cardCode)) {
      return rowDrafts[cardCode];
    }

    return {
      cardCode: fallbackItem?.cardCode || '',
      setCode: fallbackItem?.setCode || setCodeFromCardCode(fallbackItem?.cardCode),
      printInfo: fallbackItem?.printInfo || '',
      printPriceUsd: fallbackItem?.printPriceUsd ?? null,
      printVariantNumber: fallbackItem?.printVariantNumber ?? null,
      tcgPlayerUrl: fallbackItem?.tcgPlayerUrl || '',
      printOptionKey: '',
      quantity: Number(fallbackItem?.quantity || 0),
      totalInvestedUsd: Number(fallbackItem?.totalInvestedUsd || 0)
    };
  };

  const setRowDraftField = (cardCode, field, value) => {
    setRowDrafts((prev) => {
      const current = prev[cardCode] || {
        cardCode, setCode: '', printInfo: '', printPriceUsd: null, printVariantNumber: null, tcgPlayerUrl: '', printOptionKey: '', quantity: 0, totalInvestedUsd: 0
      };
      return {
        ...prev,
        [cardCode]: {
          ...current,
          [field]: value
        }
      };
    });
  };

  const saveRowEdit = (cardCode, fallbackItem) => runTask(async () => {
    const draft = getRowDraft(cardCode, fallbackItem);
    const nextCardCode = String(draft.cardCode || '').trim().toUpperCase();
    const setCode = String(draft.setCode || '').trim().toUpperCase();
    const printInfo = String(draft.printInfo || '').trim();
    const printPriceUsd = draft.printPriceUsd === null || draft.printPriceUsd === undefined
      ? null
      : Math.max(0, Number(draft.printPriceUsd || 0));
    const printVariantNumber = draft.printVariantNumber == null ? null : Number(draft.printVariantNumber);
    const tcgPlayerUrl = String(draft.tcgPlayerUrl || '').trim();
    const quantity = Math.max(0, Number(draft.quantity || 0));
    const totalInvestedUsd = Math.max(0, Number(draft.totalInvestedUsd || 0));

    const res = await fetch(`/api/collection/${encodeURIComponent(cardCode)}/entry`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ cardCode: nextCardCode, setCode, printInfo, printPriceUsd, printVariantNumber, tcgPlayerUrl, quantity, totalInvestedUsd })
    });
    const data = await res.json();
    if (!data.success) {
      throw new Error(data.message || 'No se pudo guardar la carta');
    }

    cancelRowEdit(cardCode);
    setMessage(`${cardCode}: ${data.message}`);
    await loadCollection();
    loadDashboard().catch(() => {
      // background refresh for dashboard metrics
    });
  });

  const loadCardHistory = async (entryReference, cardLabel, range = historyRange) => {
    const ref = String(entryReference || '').trim();
    if (!ref) {
      setHistoryError('Referencia invalida para historial');
      return;
    }

    setHistoryRef(ref);
    setHistoryCard(cardLabel || ref);
    setHistoryRange(range);
    setHistoryPoints([]);
    setHistoryError('');
    setHistoryLoading(true);

    try {
      const res = await fetch(`/api/collection/${encodeURIComponent(ref)}/history?range=${encodeURIComponent(range)}`);
      if (!res.ok) {
        throw new Error(`No se pudo cargar historial (${res.status})`);
      }

      const data = await res.json();
      const points = Array.isArray(data) ? data : [];
      setHistoryPoints(points);
      setHistoryLoadedAt(new Date().toISOString());
    } catch (err) {
      setHistoryError(err.message || 'No se pudo cargar el historial');
    } finally {
      setHistoryLoading(false);
    }
  };

  const runScan = () => runTask(async () => {
    if (!scanFile) {
      throw new Error('Sube una imagen primero');
    }

    const form = new FormData();
    form.append('image', scanFile);
    form.append('quantity', String(Math.max(1, Number(scanQty || 1))));

    const res = await fetch('/api/collection/scan', {
      method: 'POST',
      body: form
    });
    const data = await res.json();
    if (!data.success) {
      throw new Error(data.message || 'No se pudo detectar');
    }

    if (Array.isArray(data.addedCards) && data.addedCards.length > 0) {
      const summary = data.addedCards
        .filter((x) => x.success)
        .map((x) => `${x.cardCode} x${x.quantity}`)
        .join(', ');
      setMessage(`${data.message || 'Escaneo completado.'} ${summary ? `(${summary})` : ''}`);
    } else {
      setMessage(`Detectada ${data.detectedCode} por ${data.detectionSource}.`);
    }

    await Promise.all([loadCollection(), loadDashboard()]);
  });

  const onSearch = (e) => {
    const value = e.target.value;
    setSearch(value);
    runTask(async () => {
      await searchCards(value);
    });
  };

  return (
    <div className="app-shell">
      <header className="hero">
        <div>
          <p className="eyebrow">One Piece TCG Collection Intelligence</p>
          <h1>CardTracker</h1>
          <p className="hero-sub">Escanea cartas, registra tu coleccion y monitorea precio actual, ATH y tendencia con datos de Limitless.</p>
        </div>
        <button className="btn-main" disabled={busy} onClick={syncCatalog}>Sincronizar Catalogo Oficial</button>
      </header>

      <nav className="tab-row">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      {message && <div className="notice">{message}</div>}

      {activeTab === 'dashboard' && (
        <section className="panel-grid">
          <article className="panel wide">
            <h3>Refresh Backend</h3>
            <p className="hint">
              Ultimo refresh: {dateTime(dashboard?.lastCollectionRefreshUtc)} | Ultimo refresh con cambios: {dateTime(dashboard?.lastCollectionChangeUtc)}
            </p>
          </article>
          <article className="kpi">
            <h3>Valor Total</h3>
            <p>{money(dashboard?.totalValueUsd)}</p>
          </article>
          <article className="kpi">
            <h3>Valor ATH</h3>
            <p>{money(dashboard?.analysis?.athPortfolioValueUsd)}</p>
          </article>
          <article className="kpi">
            <h3>Drawdown</h3>
            <p className={Number(dashboard?.analysis?.drawdownFromAthPercent) >= 0 ? 'up' : 'down'}>{pct(dashboard?.analysis?.drawdownFromAthPercent)}</p>
          </article>
          <article className="kpi">
            <h3>Variacion 7d</h3>
            <p className={Number(dashboard?.analysis?.weeklyPortfolioChangePercent) >= 0 ? 'up' : 'down'}>{pct(dashboard?.analysis?.weeklyPortfolioChangePercent)}</p>
          </article>
          <article className="kpi">
            <h3>Invertido</h3>
            <p>{money(dashboard?.analysis?.totalInvestedUsd)}</p>
          </article>
          <article className="kpi">
            <h3>Profit Total</h3>
            <p className={Number(dashboard?.analysis?.profitUsd) >= 0 ? 'up' : 'down'}>
              {money(dashboard?.analysis?.profitUsd)} ({pct(dashboard?.analysis?.profitPercent)})
            </p>
          </article>

          <article className="panel wide">
            <h3>Tendencia de Portafolio (USD)</h3>
            <svg viewBox="0 0 700 220" className="trend-chart" role="img" aria-label="Tendencia del portafolio">
              <path d={trendPath} />
            </svg>
          </article>

          <article className="panel">
            <h3>Top Subidas</h3>
            <ul className="movers">
              {(dashboard?.topGainers || []).map((x, idx) => (
                <li key={`g-${x.cardCode}-${idx}`}>
                  <span>{x.name} ({x.cardCode}) [{x.trendSignal}]</span>
                  <strong className="up">{pct(x.changePercent)}</strong>
                </li>
              ))}
            </ul>
          </article>

          <article className="panel">
            <h3>Top Bajadas</h3>
            <ul className="movers">
              {(dashboard?.topLosers || []).map((x, idx) => (
                <li key={`l-${x.cardCode}-${idx}`}>
                  <span>{x.name} ({x.cardCode}) [{x.trendSignal}]</span>
                  <strong className="down">{pct(x.changePercent)}</strong>
                </li>
              ))}
            </ul>
          </article>
        </section>
      )}

      {activeTab === 'collection' && (
        <section className="panel">
          <h3>Mi Coleccion</h3>
          <div className="collection-filters">
            <input
              value={collectionFilterText}
              onChange={(e) => setCollectionFilterText(e.target.value)}
              placeholder="Buscar por carta, codigo, set, print..."
            />
            <select value={collectionFilterSet} onChange={(e) => setCollectionFilterSet(e.target.value)}>
              <option value="">Todos los sets</option>
              {collectionSets.map((setCode) => (
                <option key={`filter-set-${setCode}`} value={setCode}>{setCode}</option>
              ))}
            </select>
            <select value={collectionFilterRarity} onChange={(e) => setCollectionFilterRarity(e.target.value)}>
              <option value="">Todas las rarezas</option>
              {collectionRarities.map((rarity) => (
                <option key={`filter-rarity-${rarity}`} value={rarity}>{rarity}</option>
              ))}
            </select>
            <input
              value={collectionFilterPrint}
              onChange={(e) => setCollectionFilterPrint(e.target.value)}
              placeholder="Filtro print (ej. anniversary, manga...)"
            />
            <label className="filter-check">
              <input
                type="checkbox"
                checked={collectionFilterProfitable}
                onChange={(e) => setCollectionFilterProfitable(e.target.checked)}
              />
              Solo profit
            </label>
            <button
              className="tab-btn"
              onClick={() => {
                setCollectionFilterText('');
                setCollectionFilterSet('');
                setCollectionFilterRarity('');
                setCollectionFilterPrint('');
                setCollectionFilterProfitable(false);
              }}
            >
              Limpiar filtros
            </button>
          </div>
          <p className="hint filter-summary">
            Mostrando {filteredCollection.length} de {collection.length} cartas ({filteredCopies} de {totalCopies} copias).
          </p>
          <div className="history-panel history-panel-inline">
            <div className="history-header">
              <h4>{historyCard ? `Historial USD: ${historyCard}` : 'Historial de carta seleccionada'}</h4>
              <div className="history-ranges">
                {['24h', '7d', '30d', 'all'].map((r) => (
                  <button
                    key={r}
                    className={`tab-btn ${historyRange === r ? 'active' : ''}`}
                    disabled={!historyRef || historyLoading}
                    onClick={() => {
                      loadCardHistory(historyRef, historyCard, r);
                    }}
                  >
                    {r}
                  </button>
                ))}
                <button
                  className="tab-btn"
                  disabled={!historyRef || historyLoading}
                  onClick={() => {
                    loadCardHistory(historyRef, historyCard, historyRange);
                  }}
                >
                  Actualizar
                </button>
              </div>
            </div>
            {!historyCard ? (
              <p className="hint">Selecciona una carta y pulsa "Historial" para ver su curva de precio.</p>
            ) : (
              <>
                <div className="history-meta">
                  {historyLoading && <span className="history-status">Cargando...</span>}
                  {!historyLoading && historyError && <span className="history-status history-status-error">{historyError}</span>}
                  {!historyLoading && !historyError && (
                    <span className="history-status">
                      {historyPoints.length} puntos cargados{historyLoadedAt ? `, actualizado ${new Date(historyLoadedAt).toLocaleTimeString()}` : ''}.
                    </span>
                  )}
                </div>
                {!historyLoading && !historyError && historyPoints.length > 0 ? (
                  <>
                    <svg viewBox="0 0 700 160" className="trend-chart" role="img" aria-label={`Historial de precio USD de ${historyCard}`}>
                      <path d={historyPath} />
                    </svg>
                    <div className="table-wrap">
                      <table>
                        <thead>
                          <tr>
                            <th>Fecha UTC</th>
                            <th>Precio USD</th>
                            <th>Origen</th>
                          </tr>
                        </thead>
                        <tbody>
                          {historyPoints.slice().reverse().slice(0, 25).map((point, idx) => (
                            <tr key={`${historyCard}-${idx}`}>
                              <td>{point.timestampUtc || point.date}</td>
                              <td>{money(point.priceUsd)}</td>
                              <td>{point.source || 'n/d'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                ) : null}
              </>
            )}
          </div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Img</th>
                  {renderCollectionSortHeader('Carta', 'card')}
                  {renderCollectionSortHeader('Set', 'set')}
                  {renderCollectionSortHeader('Print', 'print')}
                  <th>Rareza</th>
                  {renderCollectionSortHeader('Cantidad', 'quantity')}
                  {renderCollectionSortHeader('Costo Unit', 'costUnit')}
                  {renderCollectionSortHeader('Precio', 'price')}
                  {renderCollectionSortHeader('ATH', 'ath')}
                  {renderCollectionSortHeader('24h', 'change24h')}
                  {renderCollectionSortHeader('Invertido', 'invested')}
                  {renderCollectionSortHeader('Profit', 'profit')}
                  {renderCollectionSortHeader('Valor', 'value')}
                  <th className="actions-cell"></th>
                </tr>
              </thead>
              <tbody>
                {filteredCollection.map((item) => (
                  <tr key={item.entryKey || item.cardCode} ref={(el) => { rowRefs.current[item.entryKey || item.cardCode] = el; }}>
                    <td>
                      {item.imageUrl
                        ? <img className="card-thumb" src={item.imageUrl} alt={item.cardCode} loading="lazy" />
                        : <span className="thumb-empty">-</span>}
                    </td>
                    <td>
                      {isEditingRow(item.entryKey || item.cardCode)
                        ? (
                          <input
                            className="row-edit-input"
                            type="text"
                            value={getRowDraft(item.entryKey || item.cardCode, item).cardCode}
                            onChange={(e) => {
                              const normalized = e.target.value.toUpperCase();
                              setRowDraftField(item.entryKey || item.cardCode, 'cardCode', normalized);
                              setRowDraftField(item.entryKey || item.cardCode, 'setCode', setCodeFromCardCode(normalized));
                            }}
                            onBlur={() => {
                              const nextCode = getRowDraft(item.entryKey || item.cardCode, item).cardCode;
                              loadRowPrintOptions(item.entryKey || item.cardCode, nextCode, item);
                            }}
                          />
                        )
                        : `${item.name} (${item.cardCode})`}
                    </td>
                    <td>{isEditingRow(item.entryKey || item.cardCode) ? (getRowDraft(item.entryKey || item.cardCode, item).setCode || '-') : item.setCode}</td>
                    <td>
                      {isEditingRow(item.entryKey || item.cardCode)
                        ? (
                          <select
                            className="row-edit-input"
                            value={getRowDraft(item.entryKey || item.cardCode, item).printOptionKey}
                            onChange={(e) => {
                              const selectedKey = e.target.value;
                              setRowDraftField(item.entryKey || item.cardCode, 'printOptionKey', selectedKey);
                              const options = rowPrintOptions[item.entryKey || item.cardCode] || [];
                              const selected = options.find((x) => makePrintOptionKey(x) === selectedKey);
                              if (selected) {
                                setRowDraftField(item.entryKey || item.cardCode, 'setCode', selected.setCode || setCodeFromCardCode(getRowDraft(item.entryKey || item.cardCode, item).cardCode));
                                setRowDraftField(item.entryKey || item.cardCode, 'printInfo', selected.printInfo);
                                setRowDraftField(item.entryKey || item.cardCode, 'printPriceUsd', selected.priceUsd ?? null);
                                setRowDraftField(item.entryKey || item.cardCode, 'printVariantNumber', selected.variantNumber ?? null);
                                setRowDraftField(item.entryKey || item.cardCode, 'tcgPlayerUrl', selected.tcgPlayerUrl || '');
                              }
                            }}
                          >
                            {((rowPrintOptions[item.entryKey || item.cardCode] || []).length === 0) && (
                              <option value="">Sin opciones</option>
                            )}
                            {(rowPrintOptions[item.entryKey || item.cardCode] || [])
                              .map((opt) => (
                                <option key={`${item.entryKey || item.cardCode}-print-${makePrintOptionKey(opt)}`} value={makePrintOptionKey(opt)}>
                                  {opt.variantNumber != null ? `v=${opt.variantNumber} - ` : ''}{opt.setCode ? `[${opt.setCode}] ` : ''}{opt.printInfo} {opt.priceUsd != null ? `($${Number(opt.priceUsd).toFixed(2)})` : '(N/A)'}
                                </option>
                              ))}
                          </select>
                        )
                        : (
                          <>
                            {item.printInfo || '-'}
                            {item.tcgPlayerUrl ? (
                              <> <a href={item.tcgPlayerUrl} target="_blank" rel="noreferrer">TCG</a></>
                            ) : null}
                          </>
                        )}
                    </td>
                    <td>
                      {item.rarity}
                    </td>
                    <td>
                      {isEditingRow(item.entryKey || item.cardCode)
                        ? (
                          <input
                            className="row-edit-input"
                            type="number"
                            min="0"
                            step="1"
                            value={getRowDraft(item.entryKey || item.cardCode, item).quantity}
                            onChange={(e) => setRowDraftField(item.entryKey || item.cardCode, 'quantity', e.target.value)}
                          />
                        )
                        : item.quantity}
                    </td>
                    <td>
                      {isEditingRow(item.entryKey || item.cardCode)
                        ? money(Number(getRowDraft(item.entryKey || item.cardCode, item).quantity || 0) > 0
                          ? Number(getRowDraft(item.entryKey || item.cardCode, item).totalInvestedUsd || 0) / Number(getRowDraft(item.entryKey || item.cardCode, item).quantity || 1)
                          : 0)
                        : money(item.avgAcquiredPriceUsd)}
                    </td>
                    <td>{money(item.unitPriceUsd)}</td>
                    <td>{money(item.athUsd)}</td>
                    <td className={Number(item.dailyChangePercent) >= 0 ? 'up' : 'down'}>{pct(item.dailyChangePercent)}</td>
                    <td>
                      {isEditingRow(item.entryKey || item.cardCode)
                        ? (
                          <input
                            className="row-edit-input"
                            type="number"
                            min="0"
                            step="0.01"
                            value={getRowDraft(item.entryKey || item.cardCode, item).totalInvestedUsd}
                            onChange={(e) => setRowDraftField(item.entryKey || item.cardCode, 'totalInvestedUsd', e.target.value)}
                          />
                        )
                        : money(item.totalInvestedUsd)}
                    </td>
                    <td className={Number(item.profitUsd) >= 0 ? 'up' : 'down'}>{money(item.profitUsd)} ({pct(item.profitPercent)})</td>
                    <td>{money(item.totalValueUsd)}</td>
                    <td className="actions-cell">
                      {isEditingRow(item.entryKey || item.cardCode)
                        ? (
                          <div className="row-actions">
                            <button className="qty-save-btn" disabled={busy} onClick={() => saveRowEdit(item.entryKey || item.cardCode, item)}>
                              <svg viewBox="0 0 24 24" aria-hidden="true" className="save-icon">
                                <path d="M5 3h11l3 3v15H5V3zm2 2v5h9V6.5L15.5 5H7zm1 9h8v5H8v-5z"></path>
                              </svg>
                            </button>
                            <button className="tab-btn" disabled={busy} onClick={() => cancelRowEdit(item.entryKey || item.cardCode)}>Cancel</button>
                            <button className="btn-danger" disabled={busy} onClick={() => removeCard(item.entryKey || item.cardCode, item.cardCode)}>Delete</button>
                          </div>
                        )
                        : (
                          <div className="row-actions">
                            <button className="btn-main" onClick={() => loadCardHistory(item.entryKey || item.cardCode, `${item.name} (${item.cardCode})`)}>Historial</button>
                            <button className="tab-btn" onClick={() => { startRowEdit(item); }}>Edit</button>
                          </div>
                        )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

        </section>
      )}

      {activeTab === 'watchlist' && (
        <section className="panel watchlist-panel">
          <div className="watchlist-header">
            <div>
              <h3>Watchlist de Tiendas Locales</h3>
              <p className="hint">El primer scan de cada tienda crea baseline y no manda alertas. Desde el segundo scan avisa nuevos productos, restock o baja de precio.</p>
            </div>
            <button className="tab-btn" disabled={busy} onClick={() => runTask(loadWatchlist)}>Actualizar</button>
          </div>

          <div className="watch-global-settings">
            <div>
              <h4>Intereses globales</h4>
              <p className="hint">
                Se aplican a todas las tiendas. Las exclusiones
                globales son opcionales.
              </p>
            </div>

            <div className="watch-global-settings-grid">
              <input
                value={watchlistSettingsDraft.globalKeywordsText}
                onChange={(e) => setWatchlistSettingsDraft({
                  ...watchlistSettingsDraft,
                  globalKeywordsText: e.target.value
                })}
                placeholder="Keywords globales"
              />

              <input
                value={
                  watchlistSettingsDraft
                    .globalExcludedKeywordsText
                }
                onChange={(e) => setWatchlistSettingsDraft({
                  ...watchlistSettingsDraft,
                  globalExcludedKeywordsText: e.target.value
                })}
                placeholder="Exclusiones globales opcionales"
              />

              <button
                className="btn-main"
                disabled={busy}
                onClick={saveWatchlistSettings}
              >
                Guardar filtros
              </button>
            </div>
          </div>

          <div className="watch-form">
            <input value={storeDraft.name} onChange={(e) => setStoreDraft({ ...storeDraft, name: e.target.value })} placeholder="Nombre tienda" />
            <input value={storeDraft.url} onChange={(e) => setStoreDraft({ ...storeDraft, url: e.target.value })} placeholder="https://tienda.cl" />
            <select value={storeDraft.provider} onChange={(e) => setStoreDraft({ ...storeDraft, provider: e.target.value })}>
              <option value="auto">Auto</option>
              <option value="shopify">Shopify</option>
              <option value="tiendanube">Tiendanube</option>
              <option value="jumpseller">Jumpseller</option>
              <option value="bsale">Bsale</option>
              <option value="prestashop">Prestashop</option>
              <option value="sitemap">Sitemap/HTML</option>
            </select>
            <label className="watch-override-toggle">
              <input
                type="checkbox"
                checked={storeDraft.useStoreOverrides}
                onChange={(e) => setStoreDraft({
                  ...storeDraft,
                  useStoreOverrides: e.target.checked
                })}
              />
              Agregar filtros específicos para esta tienda
            </label>

            <input
              disabled={!storeDraft.useStoreOverrides}
              value={storeDraft.keywordsText}
              onChange={(e) => setStoreDraft({
                ...storeDraft,
                keywordsText: e.target.value
              })}
              placeholder="Keywords extra"
            />

            <input
              disabled={!storeDraft.useStoreOverrides}
              value={storeDraft.excludedKeywordsText}
              onChange={(e) => setStoreDraft({
                ...storeDraft,
                excludedKeywordsText: e.target.value
              })}
              placeholder="Exclusiones extra"
            />
            <input type="number" min="0" value={storeDraft.maxPriceClp} onChange={(e) => setStoreDraft({ ...storeDraft, maxPriceClp: e.target.value })} placeholder="Precio max CLP" />
            <input type="number" min="5" value={storeDraft.checkIntervalMinutes} onChange={(e) => setStoreDraft({ ...storeDraft, checkIntervalMinutes: e.target.value })} title="Intervalo minutos" />
            <input type="number" min="5" max="200" value={storeDraft.maxProductsPerCheck} onChange={(e) => setStoreDraft({ ...storeDraft, maxProductsPerCheck: e.target.value })} title="Max productos por scan" />
            <button className="btn-main" disabled={busy} onClick={() => addWatchedStore()}>Agregar Tienda</button>
          </div>

          <div className="table-wrap watch-table">
            <table>
              <thead>
                <tr>
                  <th>Tienda</th>
                  <th>Provider</th>
                  <th>Keywords</th>
                  <th>Excluir</th>
                  <th>Intervalo</th>
                  <th>Ultimo scan</th>
                  <th>Estado</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {watchedStores.map((store) => (
                  <tr key={store.id}>
                    <td>
                      {editingStoreId === store.id ? (
                        <input value={storeEditDraft?.name || ''} onChange={(e) => setStoreEditDraft({ ...storeEditDraft, name: e.target.value })} />
                      ) : (
                        <a href={store.url} target="_blank" rel="noreferrer">{store.name}</a>
                      )}
                    </td>
                    <td>{editingStoreId === store.id ? (
                      <select value={storeEditDraft?.provider || 'auto'} onChange={(e) => setStoreEditDraft({ ...storeEditDraft, provider: e.target.value })}>
                        <option value="auto">Auto</option>
                        <option value="shopify">Shopify</option>
                        <option value="tiendanube">Tiendanube</option>
                        <option value="jumpseller">Jumpseller</option>
                        <option value="bsale">Bsale</option>
                        <option value="prestashop">Prestashop</option>
                        <option value="sitemap">Sitemap/HTML</option>
                      </select>
                    ) : store.provider}</td>
                    <td>{editingStoreId === store.id ? (
                      <input value={storeEditDraft?.keywordsText || ''} onChange={(e) => setStoreEditDraft({ ...storeEditDraft, keywordsText: e.target.value })} />
                    ) : ((store.keywords || []).join(', ') || '-')}</td>
                    <td>{editingStoreId === store.id ? (
                      <input value={storeEditDraft?.excludedKeywordsText || ''} onChange={(e) => setStoreEditDraft({ ...storeEditDraft, excludedKeywordsText: e.target.value })} />
                    ) : ((store.excludedKeywords || []).join(', ') || '-')}</td>
                    <td>{editingStoreId === store.id ? (
                      <input type="number" min="5" value={storeEditDraft?.checkIntervalMinutes || 30} onChange={(e) => setStoreEditDraft({ ...storeEditDraft, checkIntervalMinutes: e.target.value })} />
                    ) : `${store.checkIntervalMinutes} min`}</td>
                    <td>{dateTime(store.lastCheckedUtc)}</td>
                    <td>
                      <span className={store.enabled ? 'up' : 'down'}>{store.enabled ? 'Activa' : 'Pausada'}</span>
                      <br />
                      <small>{store.lastError || store.lastStatus || (store.baselineComplete ? 'Baseline listo' : 'Sin baseline')}</small>
                    </td>
                    <td className="actions-cell">
                      <div className="row-actions">
                        {editingStoreId === store.id ? (
                          <>
                            <button className="btn-main" disabled={busy} onClick={() => saveStoreEdit(store)}>Guardar</button>
                            <button className="tab-btn" disabled={busy} onClick={cancelStoreEdit}>Cancelar</button>
                          </>
                        ) : (
                          <>
                            <button className="tab-btn" disabled={busy} onClick={() => scanWatchedStore(store.id)}>Scan</button>
                            <button className="tab-btn" disabled={busy} onClick={() => startStoreEdit(store)}>Editar</button>
                            <button className="tab-btn" disabled={busy} onClick={() => toggleWatchedStore(store)}>{store.enabled ? 'Pausar' : 'Activar'}</button>
                            <button className="btn-danger" disabled={busy} onClick={() => deleteWatchedStore(store.id)}>Delete</button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="watch-products-header">
            <div>
              <h3>Productos Vistos</h3>
              <p className="hint">
                Mostrando {
                  filteredWatchedProducts.length === 0
                    ? 0
                    : (safeWatchProductPage - 1) * watchPageSize + 1
                }
                {' '}a {
                  Math.min(
                    safeWatchProductPage * watchPageSize,
                    filteredWatchedProducts.length
                  )
                }
                {' '}de {filteredWatchedProducts.length}.
                {' '}Seleccionados: {
                  Object.keys(selectedWatchProductKeys).length
                }.
              </p>
            </div>

            <div className="watch-products-actions">
              <button
                className="tab-btn"
                disabled={busy || filteredWatchedProducts.length === 0}
                onClick={selectVisibleWatchProducts}
              >
                Seleccionar visibles
              </button>

              <button
                className="tab-btn"
                disabled={
                  busy
                  || Object.keys(selectedWatchProductKeys).length === 0
                }
                onClick={clearWatchProductSelection}
              >
                Limpiar selección
              </button>

              <button
                className="btn-danger"
                disabled={
                  busy
                  || Object.keys(selectedWatchProductKeys).length === 0
                }
                onClick={deleteSelectedWatchProducts}
              >
                Eliminar seleccionados
              </button>

              <input
                value={watchSearchText}
                placeholder="Buscar producto"
                onChange={(e) => {
                  setWatchSearchText(e.target.value);
                  setWatchProductPage(1);
                }}
              />

              <select
                value={watchStoreFilter}
                onChange={(e) => {
                  setWatchStoreFilter(e.target.value);
                  setWatchProductPage(1);
                }}
              >
                <option value="all">Todas las tiendas</option>
                {watchedStores.map((store) => (
                  <option key={store.id} value={store.id}>
                    {store.name}
                  </option>
                ))}
              </select>

              <select
                value={watchStockFilter}
                onChange={(e) => {
                  setWatchStockFilter(e.target.value);
                  setWatchProductPage(1);
                }}
              >
                <option value="all">Todos</option>
                <option value="in">Con stock</option>
                <option value="out">Sin stock</option>
                <option value="unknown">Stock N/D</option>
              </select>
            </div>
          </div>
          <div className="product-grid-watch">
            {pagedWatchedProducts.map((product) => {
              const store = watchedStores.find((x) => x.id === product.storeId);
              return (
                <article
                  className={
                    `watch-product${
                      selectedWatchProductKeys[watchProductKey(product)]
                        ? ' selected'
                        : ''
                    }`
                  }
                  key={`${product.storeId}-${product.externalId}`}
                >
                  <input
                    className="watch-product-select"
                    type="checkbox"
                    aria-label={`Seleccionar ${product.title}`}
                    checked={Boolean(
                      selectedWatchProductKeys[watchProductKey(product)]
                    )}
                    onChange={() => toggleWatchProductSelection(product)}
                  />
                  {product.imageUrl ? <img src={product.imageUrl} alt="" loading="lazy" /> : <div className="watch-product-empty" />}
                  <div>
                    <a href={product.url} target="_blank" rel="noreferrer">{product.title}</a>
                    <p>{store?.name || 'Tienda'} | {clp(product.price)} | {product.inStock === true ? 'En stock' : product.inStock === false ? 'Sin stock' : 'Stock N/D'}</p>
                    <small>Visto: {dateTime(product.lastSeenUtc)}</small>
                    <div className="watch-product-links">
                      <a className="direct-button" href={product.url} target="_blank" rel="noreferrer">Abrir producto</a>
                      {store?.url ? <a className="direct-link" href={store.url} target="_blank" rel="noreferrer">Tienda</a> : null}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
          <div className="watch-product-pagination">
            <button
              className="tab-btn"
              disabled={busy || safeWatchProductPage <= 1}
              onClick={() => setWatchProductPage(
                Math.max(1, safeWatchProductPage - 1)
              )}
            >
              Anterior
            </button>

            <span>
              Página {safeWatchProductPage} de {watchPageCount}
            </span>

            <button
              className="tab-btn"
              disabled={
                busy || safeWatchProductPage >= watchPageCount
              }
              onClick={() => setWatchProductPage(
                Math.min(
                  watchPageCount,
                  safeWatchProductPage + 1
                )
              )}
            >
              Siguiente
            </button>
          </div>

        </section>
      )}


      {activeTab === 'settings' && (
        <section className="panel settings-panel">
          <div className="watchlist-header">
            <div>
              <h3>Configuracion</h3>
              <p className="hint">Conexiones y servicios que usa la app para bot, alertas y escaneo IA.</p>
            </div>
            <button className="tab-btn" disabled={busy} onClick={() => runTask(loadSettings)}>Actualizar</button>
          </div>

          <div className="settings-grid">
            <section className="settings-section">
              <div className="settings-section-head">
                <div>
                  <h3>Telegram Bot</h3>
                  <p className="hint">Token y chat autorizado para respuestas del bot y alertas de watchlist.</p>
                </div>
                <span className={`status-pill ${telegramStatus?.canSendAlerts ? 'ok' : 'warn'}`}>
                  {telegramStatus?.canSendAlerts ? 'OK' : 'Revisar'}
                </span>
              </div>
              <p className="settings-status">
                {telegramStatus?.message || 'Sin revisar'}{telegramStatus?.botUsername ? ` (@${telegramStatus.botUsername})` : ''}
              </p>
              <div className="settings-details">
                <div><span>Token</span><strong>{telegramStatus?.maskedBotToken || 'No configurado'}</strong></div>
                <div><span>Origen</span><strong>{telegramStatus?.botTokenSource || 'N/D'}</strong></div>
                <div><span>Bot ID</span><strong>{telegramStatus?.botId || 'N/D'}</strong></div>
                <div><span>Nombre</span><strong>{telegramStatus?.botFirstName || telegramStatus?.botUsername || 'N/D'}</strong></div>
                <div><span>Chat IDs</span><strong>{(telegramStatus?.allowedChatIds || []).length ? telegramStatus.allowedChatIds.join(', ') : 'N/D'}</strong></div>
              </div>
              <div className="settings-form telegram-form">
                <input
                  type="password"
                  value={telegramDraft.botToken}
                  onChange={(e) => setTelegramDraft({ ...telegramDraft, botToken: e.target.value })}
                  placeholder={telegramStatus?.configured ? 'Token configurado' : 'Bot token'}
                  autoComplete="off"
                />
                <input
                  type="number"
                  value={telegramDraft.allowedChatId}
                  onChange={(e) => setTelegramDraft({ ...telegramDraft, allowedChatId: e.target.value })}
                  placeholder={telegramStatus?.allowedChatCount ? 'Chat id configurado' : 'Chat id permitido'}
                />
                <button className="btn-main" disabled={busy} onClick={saveTelegramSettings}>Guardar Telegram</button>
              </div>
            </section>

            <section className="settings-section">
              <div className="settings-section-head">
                <div>
                  <h3>Codex Scanner</h3>
                  <p className="hint">Control del analisis IA de imagenes del bot y del escaner web.</p>
                </div>
                <span className={`status-pill ${codexStatus?.effectiveEnabled && codexStatus?.authenticated ? 'ok' : 'warn'}`}>
                  {codexStatus?.effectiveEnabled && codexStatus?.authenticated ? 'OK' : 'Revisar'}
                </span>
              </div>
              <p className="settings-status">{codexStatus?.message || 'Sin revisar'}</p>

              <div className="settings-form codex-form">
                <select value={codexDraft.enabled} onChange={(e) => setCodexDraft({ ...codexDraft, enabled: e.target.value })}>
                  <option value="">Usar config base</option>
                  <option value="true">Activar</option>
                  <option value="false">Desactivar</option>
                </select>
                <label className="settings-check"><input type="checkbox" checked={codexDraft.validateLogin} onChange={(e) => setCodexDraft({ ...codexDraft, validateLogin: e.target.checked })} /> validar al guardar</label>
                <button className="btn-main" disabled={busy} onClick={saveCodexSettings}>Guardar Codex</button>
                <button className="tab-btn" disabled={busy} onClick={validateCodexLogin}>Validar login</button>
              </div>

              <div className="codex-login-panel">
                <div>
                  <h4>Login web de Codex</h4>
                  <p className="hint">Inicia el login, abre el link y escribe el codigo antes de que expire.</p>
                </div>
                <div className="codex-login-actions">
                  <button className="btn-main" disabled={busy} onClick={startCodexLogin}>Iniciar login web</button>
                  {codexLogin?.loginUrl ? <a className="direct-button" href={codexLogin.loginUrl} target="_blank" rel="noreferrer">Abrir login</a> : null}
                </div>
                <div className={`device-code-box ${codexLogin?.deviceCode ? 'active' : ''}`}>
                  <span>Codigo de validacion</span>
                  <strong>{codexLogin?.deviceCode || 'Pendiente'}</strong>
                  <small>{codexStatus?.loginInProgress ? 'Login en progreso' : 'Sin login iniciado'}</small>
                </div>
                {codexLogin?.rawOutput ? <pre className="codex-login-output">{codexLogin.rawOutput}</pre> : null}
              </div>
            </section>
          </div>
        </section>
      )}

      {activeTab === 'manual' && (
        <section className="panel two-col">
          <div>
            <h3>Buscar Carta</h3>
            <input value={search} onChange={onSearch} placeholder="Nombre, codigo o set..." />
            <div className="list-box">
              {cards.map((card) => (
                <button
                  key={card.code}
                  className={`list-item ${selectedCode === card.code ? 'selected' : ''}`}
                  onClick={() => setSelectedCode(card.code)}
                >
                  <span>{card.name}</span>
                  <small>
                    {card.code} | {card.rarity} | {card.printInfo || '-'} | {money(card.printPriceUsd ?? card.lastPriceUsd)}
                  </small>
                </button>
              ))}
            </div>
          </div>

          <div>
            <h3>Agregar a Coleccion</h3>
            <label>Codigo seleccionado</label>
            <input value={selectedCode} onChange={(e) => setSelectedCode(e.target.value.toUpperCase())} placeholder="OP01-001" />
            <label>Cantidad</label>
            <input type="number" min="1" value={manualQty} onChange={(e) => setManualQty(e.target.value)} />
            <label>Variante / Print</label>
            <select value={manualPrintKey} onChange={(e) => setManualPrintKey(e.target.value)} disabled={!selectedCode || manualLoadingPrints}>
              {manualLoadingPrints && <option value="">Cargando variantes...</option>}
              {!manualLoadingPrints && manualPrintOptions.length === 0 && <option value="">Sin opciones</option>}
              {manualPrintOptions.map((opt) => (
                <option key={`manual-print-${makePrintOptionKey(opt)}`} value={makePrintOptionKey(opt)}>
                  {opt.variantNumber != null ? `v=${opt.variantNumber} - ` : ''}{opt.setCode ? `[${opt.setCode}] ` : ''}{opt.printInfo} {opt.priceUsd != null ? `($${Number(opt.priceUsd).toFixed(2)})` : '(N/A)'}
                </option>
              ))}
            </select>
            {selectedManualPrint && (
              <p className="hint manual-print-hint">
                Seleccionado: {selectedManualPrint.printInfo}
                {selectedManualPrint.variantNumber != null ? ` (v=${selectedManualPrint.variantNumber})` : ''}
                {selectedManualPrint.priceUsd != null ? ` - ${money(selectedManualPrint.priceUsd)}` : ' - Precio N/A'}
                {selectedManualPrint.tcgPlayerUrl ? (
                  <> <a href={selectedManualPrint.tcgPlayerUrl} target="_blank" rel="noreferrer">TCG</a></>
                ) : null}
              </p>
            )}
            <label>Precio de adquisicion (USD)</label>
            <input type="number" min="0" step="0.01" value={manualBuyPrice} onChange={(e) => setManualBuyPrice(e.target.value)} />
            <button className="btn-main" disabled={busy || !selectedCode} onClick={addManual}>Agregar</button>
          </div>
        </section>
      )}

      {activeTab === 'scanner' && (
        <section className="panel two-col">
          <div>
            <h3>Escaner por Imagen</h3>
            <p className="hint">Si la vision IA no esta disponible, se usa fallback por nombre de archivo.</p>
            <input type="file" accept="image/*" onChange={(e) => setScanFile(e.target.files?.[0] || null)} />
            <label>Cantidad</label>
            <input type="number" min="1" value={scanQty} onChange={(e) => setScanQty(e.target.value)} />
            <button className="btn-main" disabled={busy} onClick={runScan}>Escanear y Agregar</button>
          </div>
          <div className="preview">
            {scanFile ? <img src={URL.createObjectURL(scanFile)} alt="Preview de carta" /> : <p>Sube una imagen para previsualizar.</p>}
          </div>
        </section>
      )}
    </div>
  );
}
